import type { CSSProperties, ReactNode } from 'react';

/**
 * Shared HUD pill button — the canonical translucent-navy + gold-rim
 * pill for the app's HUD / navigation chrome (episodes back, social
 * pills, the circular back button, …).
 *
 * <p>Extracted 2026-06-07 (producer: "all the HUDs and buttons should
 * have a consistent style across the app"). These were hand-rolled
 * inline in a dozen places and had drifted into three different rims —
 * gold (Friends/Join), blue (EPISODES), white (BackToMapButton). This
 * unifies them on the gold-rim navy pill, which also mirrors the
 * in-canvas BET / gear-info pill drawn by the Pixi {@code
 * SlotHudComponent}, so the React chrome and the canvas chrome read as
 * one family.
 *
 * <p>Hosts position the button (they're absolutely placed in their
 * parents) via {@link PillButtonProps.style}, which merges last.
 */

/** Canonical HUD-pill tokens. Reference these anywhere a pill-styled
 *  surface is hand-built (e.g. {@code BalancePill}) so the family stays
 *  in sync without forcing every chip through this component. */
export const HUD_PILL = {
  bg: 'rgba(11,24,56,0.72)',
  border: '1.5px solid rgba(255,228,138,0.4)', // gold rim
  radius: 999,
  shadow: '0 4px 14px rgba(0,0,0,0.4)',
  color: '#fff',
  font: '"Fredoka", -apple-system, system-ui, sans-serif',
} as const;

export interface PillButtonProps {
  onClick?: () => void;
  children?: ReactNode;
  /** Leading glyph/icon — an <img>, <span>, or Icon element. */
  icon?: ReactNode;
  ariaLabel?: string;
  /** sm = compact nav chrome; md = default labelled action. */
  size?: 'sm' | 'md';
  /** Uppercase + wide tracking (e.g. the EPISODES back chip). */
  uppercase?: boolean;
  /** Positioning / per-host overrides — merged last so hosts keep
   *  control of placement, z-index, fixed sizing, etc. */
  style?: CSSProperties;
}

export function PillButton({
  onClick,
  children,
  icon,
  ariaLabel,
  size = 'md',
  uppercase = false,
  style,
}: PillButtonProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 7,
        padding: size === 'sm' ? '6px 12px' : '9px 14px',
        borderRadius: HUD_PILL.radius,
        border: HUD_PILL.border,
        background: HUD_PILL.bg,
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
        color: HUD_PILL.color,
        fontFamily: HUD_PILL.font,
        fontSize: uppercase ? 11 : 13,
        fontWeight: 800,
        letterSpacing: uppercase ? 1.1 : 0.3,
        textTransform: uppercase ? 'uppercase' : 'none',
        lineHeight: 1,
        cursor: 'pointer',
        boxShadow: HUD_PILL.shadow,
        ...style,
      }}
    >
      {icon}
      {children}
    </button>
  );
}
